import { useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../configureStore';
import { fetchSiloFillLevels } from '../../store/Silo.store';
import styles from './SiloUebersicht.module.scss';
import SiloIcon from './SiloIcon';
import { routes } from '../../shared/constants';
import LoadingSpinner from '../../shared/ui-components/LoadingSpinner/LoadingSpinner';
import { useScrollRestoration } from '../../shared/hooks/useScrollRestoration';

const getDataAgeWarning = (lastUpdate: string) => {
    const lastUpdateTime = new Date(lastUpdate);
    const now = new Date();

    const germanOffset = 2 * 60 * 60 * 1000;
    const hoursDiff = Math.abs(now.getTime() + germanOffset - lastUpdateTime.getTime()) / (1000 * 60 * 60);

    if (hoursDiff > 6) {
        return {
            type: 'error',
            message: 'Daten sind veraltet (>6h)',
        };
    }
    return null;
};

export const SiloKachel = ({
    siloId,
    name,
    max,
    aktuell,
    prozent,
    lastUpdate,
}: {
    siloId: string;
    name: string;
    max: number | null;
    aktuell: number | null;
    prozent: number | null;
    lastUpdate: string | null;
}): JSX.Element => {
    const history = useHistory();
    const dataWarning = lastUpdate ? getDataAgeWarning(lastUpdate) : null;

    const formatTons = (value: number | null): string => {
        if (value === null || value === undefined) {
            return '?';
        }
        return value.toLocaleString('de-DE', { maximumFractionDigits: 1 });
    };

    const hasFillLevelHistory = aktuell !== null && lastUpdate && lastUpdate !== '';
    const percentForIcon = hasFillLevelHistory && prozent != null ? prozent : -1;

    const handleClick = () => {
        // Anzeige/History immer, sobald es Füllstände gibt (egal ob Sensor oder manuell)
        if (hasFillLevelHistory) {
            history.push(`/meine-silos/history?siloId=${siloId}`);
        } else {
            history.push(`${routes.siloEinstellungen}?siloId=${siloId}`);
        }
    };

    return (
        <div className={styles.siloKachel} onClick={handleClick} style={{ position: 'relative' }}>
            <SiloIcon percent={percentForIcon} warning={!!dataWarning} warningMessage={dataWarning?.message} width={160} height={186} />
            <div className={styles.siloName}>{name}</div>
            <div className={styles.siloStats}>
                Aktuell: {formatTons(aktuell)} t | Max: {formatTons(max)} t
            </div>
        </div>
    );
};

const SiloUebersicht = (): JSX.Element => {
    const dispatch = useDispatch();
    const { fillLevels, isLoading, error } = useSelector((state: RootState) => state.silo);
    // Reihenfolge vom Backend: ältestes Silo (ältestes Datum) zuerst, neuestes (zuletzt angelegt) zuletzt
    const sortedFillLevels = fillLevels;

    useEffect(() => {
        dispatch(fetchSiloFillLevels());
    }, [dispatch]);

    useScrollRestoration('SiloUebersicht', !isLoading && sortedFillLevels.length > 0);

    if (error) {
        return <div>Fehler beim Laden der Silos: {error}</div>;
    }

    return (
        <LoadingSpinner isLoading={isLoading}>
            <div className={styles.siloContainer}>
                <h2 className={styles.pageTitle}>Meine Silos</h2>
                <div className={`${styles.siloGrid} ${sortedFillLevels.length === 0 ? styles.empty : ''}`}>
                    {sortedFillLevels.map((silo) => (
                        <SiloKachel
                            key={silo.siloId}
                            siloId={silo.siloId}
                            name={silo.siloName}
                            max={silo.maxCapacityTons}
                            aktuell={silo.currentTons}
                            prozent={silo.currentPercent}
                            lastUpdate={silo.lastUpdate}
                        />
                    ))}
                    <Link to={routes.siloAnlegen} className={styles.addTile} aria-label="Silo hinzufügen">
                        + Silo hinzufügen
                    </Link>
                </div>
            </div>
        </LoadingSpinner>
    );
};

export default SiloUebersicht;
